'use client';

import { sendGAEvent } from '@next/third-parties/google';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCreationDetail } from '@/app/apis';
import { AgentLineGenerateVideo } from '@/app/components/icons/AgentLineGenerateVideo';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { ProductFillFrames } from '@/app/components/icons/ProductFillFrames';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { BasicTextarea } from '@/app/components/shared/BasicTextarea';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { FeatureTour } from '@/app/components/shared/FeatureTour';
import { TemplateImgCropModal } from '@/app/components/shared/ImgCropModals/TemplateImgCropModal';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { toast } from '@/app/components/ui/use-toast';
import {
  MODEL_VERSION,
  Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE,
  RECREATE_INSPIRATION_KEY,
  RECREATE_TASK_KEY,
  RESOLUTION_MAP,
  SITE,
  TASK_SETTING_OPTION,
  TOUR_KEYS,
} from '@/app/constants';
import {
  useAdsDialog,
  useDevice,
  useFetch,
  useFetchRecreateData,
  useGlobalModal,
  useImageSelectModal,
  useRunTempUploadBatch,
  useUpgradeDialogs,
} from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  CreationInfo,
  CreationWithTask,
  FormImagePrompt,
  FormVideoPrompt,
  ImagePrompt,
  ImagePromptWithSize,
  ImageUploadFile,
  SubsPlanType,
  TaskInfo,
  VideoUploadFile,
} from '@/app/types';
import type { Video2ExtendFormParams } from '@/app/utils';
import {
  buildExtensionToolDraftFromCreation,
  buildExtensionToolDraftFromTask,
  checkSourceImageSize,
  generateFromPage,
  getImageRatio,
  getImageSize,
  isUltimateOrHigherPlan,
  reportEvent,
} from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { usePathname, useRouter } from '@/i18n/routing';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { ToolSettingContainer } from '../_components/ToolSettingsOptions';
import { ToolSettingsWithConfig } from '../_components/ToolSettingsWithConfig';
import { createImagePromptFromCreation, createPromptListHelpers } from '../_utils';
import { getToolTempImagesPendingItems, getToolTempSourceVideoItems } from '../_utils/tool-temp-upload-items';
import { ExtendModelSelection } from './_components/ExtendModelSelection';
import { VideoPreview } from './_components/VideoPreview';
import { useSettings } from './_hooks/use-settings';
import { checkImageRatiosByRatios, getUserExtendLimit } from './_utils';
import { convertVideo2extend } from './_utils/convert-video2extend';
import { SETTING_KEY_CONFIG } from './constants';

enum SUBMIT_BUTTON_STATUS {
  ENABLE = 0,
  NEED_UPGRADE = 1,
  OVER_LIMIT = 2,
}

const MAX_IMAGE_SIZE = RESOLUTION_MAP['720p'];
const EXTEND_REQUIRED_VIDEO_AREA = RESOLUTION_MAP['1080p'];
const EXTEND_VIDEO_AREA_TOLERANCE = Math.round(EXTEND_REQUIRED_VIDEO_AREA * 0.005);
const EXTEND_MIN_VIDEO_DURATION_IN_SECONDS = 2;
const EXTEND_UPLOAD_MAX_VIDEO_DURATION_IN_SECONDS = 30;
const EXTEND_ACCEPT_VIDEO_MIMETYPE = ['video/mp4'];

const getGreatestCommonDivisor = (left: number, right: number): number => {
  let a = Math.round(Math.abs(left));
  let b = Math.round(Math.abs(right));

  while (b) {
    const temp = b;
    b = a % b;
    a = temp;
  }

  return a || 1;
};

const formatAspectRatioLabel = (width: number, height: number): string => {
  const divisor = getGreatestCommonDivisor(width, height);
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
};

const is1080pAreaVideo = (width?: number, height?: number): boolean => {
  if (!width || !height) {
    return false;
  }

  return Math.abs(width * height - EXTEND_REQUIRED_VIDEO_AREA) <= EXTEND_VIDEO_AREA_TOLERANCE;
};

const is1080pCreation = (creation?: Pick<CreationWithTask, 'resolution'> | null): boolean => {
  if (!creation?.resolution) {
    return false;
  }

  const { resolution, width, height } = creation.resolution;
  return resolution === '1080p' || is1080pAreaVideo(width, height);
};

const isValidCreationId = (creationId?: string): creationId is string => {
  return !!creationId && creationId !== '0';
};

const isCreationPlaceholderVideoUrl = (url?: string): boolean => {
  return !!url && (url.startsWith('sscreation:') || url.startsWith('ssupload:'));
};

const ExtensionForm: FC = () => {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const storeApi = useStoreApi();
  const searchParams = useSearchParams();
  const { getSettings } = useSettings();
  const { handleOpenPlanDialog } = useUpgradeDialogs();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { tryOpenInsufficientCreditsDialog } = useAdsDialog();
  const { isMobile } = useDevice();
  const [openMediaSelector, setOpenMediaSelector] = useState(false);
  const [selectedCreationIndex, setSelectedCreationIndex] = useState<number | null>(null);
  const [openEndFrameSelector, setOpenEndFrameSelector] = useState(false);
  const [selectedEndFrameCreationIndex, setSelectedEndFrameCreationIndex] = useState<number | null>(null);
  const [selectedEndFrameCreation, setSelectedEndFrameCreation] = useState<CreationWithTask | null>(null);
  const [uploadingVideoPrompt, setUploadingVideoPrompt] = useState<FormVideoPrompt | null>(null);
  const [isTempMediaUploading, setIsTempMediaUploading] = useState(false);
  const forceCropFallbackPromptRef = useRef<FormImagePrompt | null>(null);
  const forceCropReasonRef = useRef<'upload' | 'video-select' | null>(null);
  const hasTriggeredExtendTourRef = useRef(false);

  const preImagePromptRef = useRef<FormImagePrompt | null>(null);
  const creationId = searchParams.get('creationId') || '';
  const recreateTaskId = searchParams.get(RECREATE_TASK_KEY) || '';
  const recreateInspirationId = searchParams.get(RECREATE_INSPIRATION_KEY) || '';
  const hasRecreateParams = !!recreateTaskId || !!recreateInspirationId;

  const {
    userInfo,
    trialsQ2ExtendPro,
    trialsQ2Extend,
    activateNextTour,
    remainingTrialsQ2ExtendPro,
    remainingTrialsQ2Extend,
    isFree,
    extensionToolDraft,
    replaceExtensionToolDraft,
    updateExtensionToolDraft,
    isTeamSpace,
  } = useStore((state) => ({
    trialsQ2ExtendPro: state.trialsQ2ExtendPro.remaining,
    trialsQ2Extend: state.trialsQ2Extend.remaining,
    userInfo: state.userInfo,
    activateNextTour: state.activateNextTour,
    remainingTrialsQ2ExtendPro: state.trialsQ2ExtendPro.remaining,
    remainingTrialsQ2Extend: state.trialsQ2Extend.remaining,
    isFree: (state.userInfo?.subs_plan || 'free') === 'free',
    extensionToolDraft: state.extensionToolDraft,
    replaceExtensionToolDraft: state.replaceExtensionToolDraft,
    updateExtensionToolDraft: state.updateExtensionToolDraft,
    isTeamSpace: state.isTeamSpace,
  }));

  const { creation, rangeValue, uploadedVideo, formParams } = extensionToolDraft;
  const totalDuration = Math.ceil(creation?.duration || 0);

  const modelVersion = formParams.modelVersion;
  const settingsConfig = getSettings('video2extend', formParams.modelVersion);
  const { textPrompt, imagePrompts } = formParams;
  const hasEndFrame = !!imagePrompts[0];
  const submitTaskType: 'video2extail' | 'video2extend' = hasEndFrame ? 'video2extail' : 'video2extend';
  const settings = formParams.settings;
  const isInTrials = useMemo(() => {
    return (
      settings.sample_count === 1 &&
      modelVersion === MODEL_VERSION.Q2 &&
      ((settings.transition === 'extendspeed' && remainingTrialsQ2Extend > 0) ||
        (settings.transition === 'extendpro' && remainingTrialsQ2ExtendPro > 0))
    );
  }, [modelVersion, remainingTrialsQ2Extend, remainingTrialsQ2ExtendPro, settings.sample_count, settings.transition]);
  const submitValue = useMemo(() => {
    const taskParams = convertVideo2extend(formParams, modelVersion);

    return {
      ...taskParams,
      type: submitTaskType,
      settings: {
        ...taskParams.settings,
        use_trial: isInTrials,
      },
    };
  }, [formParams, modelVersion, submitTaskType, isInTrials]);
  const includedSettingOptions = useMemo(
    () =>
      (settingsConfig?.resolution?.length ?? 0) > 1
        ? SETTING_KEY_CONFIG
        : SETTING_KEY_CONFIG.filter((option) => option !== TASK_SETTING_OPTION.Resolution),
    [settingsConfig?.resolution],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().extensionToolDraft.formParams.imagePrompts,
          (prompts) =>
            updateExtensionToolDraft((draft) => ({
              ...draft,
              formParams: { ...draft.formParams, imagePrompts: prompts },
            })),
        ),
        { imagePrompts: formParams.imagePrompts },
      ),
    [formParams.imagePrompts, updateExtensionToolDraft, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp source video',
    (refs) =>
      getToolTempSourceVideoItems(refs, {
        sourceVideo: uploadedVideo,
        getSourceVideo: () => storeApi.getState().extensionToolDraft.uploadedVideo,
        setSourceVideo: (video) =>
          updateExtensionToolDraft((draft) => ({
            ...draft,
            uploadedVideo: video,
            formParams: {
              ...draft.formParams,
              videoPrompt: {
                ...draft.formParams.videoPrompt,
                content: video?.src || draft.formParams.videoPrompt.content,
                sourceMeta:
                  draft.formParams.videoPrompt.sourceMeta.type === 'creation'
                    ? draft.formParams.videoPrompt.sourceMeta
                    : {
                        type: 'upload',
                        uploadId: video?.id || draft.formParams.videoPrompt.sourceMeta.uploadId,
                      },
              },
            },
          })),
        onStatusChange: (status) => {
          setIsTempMediaUploading(status === 'uploading');
        },
      }),
    [updateExtensionToolDraft, uploadedVideo, userInfo],
  );

  const formValidationErrorMessage = useMemo(() => {
    if (
      modelVersion === MODEL_VERSION.Q2 &&
      isFree &&
      ((settings.transition === 'extendspeed' && remainingTrialsQ2Extend <= 0) ||
        (settings.transition === 'extendpro' && remainingTrialsQ2ExtendPro <= 0))
    ) {
      return t.rich('create.createForm.error.extendShouldUpgrade', {
        upgrade: (chunks) => (
          <span className="cursor-pointer text-system-blue02" onClick={() => handleOpenPlanDialog?.('extend_upgrade')}>
            {chunks}
          </span>
        ),
      });
    }
    return '';
  }, [
    modelVersion,
    isFree,
    remainingTrialsQ2Extend,
    remainingTrialsQ2ExtendPro,
    settings.transition,
    t,
    handleOpenPlanDialog,
  ]);

  const handleTaskSuccess = useCallback(
    (task: TaskInfo) => {
      const draft = buildExtensionToolDraftFromTask({
        task,
        taskId: task.id || recreateTaskId,
      });

      if (!draft) {
        return;
      }

      replaceExtensionToolDraft(draft);
    },
    [recreateTaskId, replaceExtensionToolDraft],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  const sourceCreationId =
    formParams.videoPrompt.sourceMeta.type === 'creation' ? formParams.videoPrompt.sourceMeta.creationId : '';
  const sourceCreationHydrationKeyRef = useRef<string>('');
  const shouldHydrateSourceCreation = useMemo(() => {
    if (!isValidCreationId(sourceCreationId)) {
      return false;
    }

    const hasResolvedCreation = creation?.id === sourceCreationId && !!creation?.uri && !!creation?.resolution;
    const hasValidDuration =
      formParams.videoPrompt.duration > 0 || totalDuration > 0 || (uploadedVideo?.duration ?? 0) > 0;
    const hasResolvedPreviewSource =
      !!(creation?.uri || uploadedVideo?.preview || uploadedVideo?.src) &&
      !isCreationPlaceholderVideoUrl(uploadedVideo?.preview) &&
      !isCreationPlaceholderVideoUrl(uploadedVideo?.src);

    return !hasResolvedCreation || !hasValidDuration || !hasResolvedPreviewSource;
  }, [
    creation?.id,
    creation?.resolution,
    creation?.uri,
    formParams.videoPrompt.duration,
    sourceCreationId,
    totalDuration,
    uploadedVideo?.duration,
    uploadedVideo?.preview,
    uploadedVideo?.src,
  ]);

  useEffect(() => {
    if (!shouldHydrateSourceCreation || !isValidCreationId(sourceCreationId)) {
      return;
    }

    const hydrationKey = `${extensionToolDraft.initializedKey || 'empty'}:${sourceCreationId}`;
    if (sourceCreationHydrationKeyRef.current === hydrationKey) {
      return;
    }

    sourceCreationHydrationKeyRef.current = hydrationKey;
    let cancelled = false;

    const hydrateSourceCreation = async () => {
      const { data, error } = await getCreationDetail({
        path: {
          id: sourceCreationId,
        },
      });

      if (cancelled || error || !data?.creation) {
        return;
      }

      const sourceCreation = data.creation;
      const sourceDuration = Math.ceil(sourceCreation.duration || 0);

      updateExtensionToolDraft((draft) => {
        if (
          draft.formParams.videoPrompt.sourceMeta.type !== 'creation' ||
          draft.formParams.videoPrompt.sourceMeta.creationId !== sourceCreationId
        ) {
          return draft;
        }

        const currentRangeFrom = draft.rangeValue[0];
        const currentRangeTo = draft.rangeValue[1];
        const nextRangeTo = currentRangeTo > currentRangeFrom ? currentRangeTo : sourceDuration;
        const nextVideoDuration =
          draft.formParams.videoPrompt.duration > 0 ? draft.formParams.videoPrompt.duration : sourceDuration;
        const resolvedVideoUrl = sourceCreation.uri || draft.uploadedVideo?.preview || draft.uploadedVideo?.src || '';

        return {
          ...draft,
          creation: sourceCreation,
          uploadedVideo: {
            id: sourceCreation.id || draft.uploadedVideo?.id || sourceCreationId,
            src: resolvedVideoUrl,
            preview: resolvedVideoUrl,
            duration: draft.uploadedVideo?.duration || sourceDuration,
            aspectRatio:
              sourceCreation.resolution?.width && sourceCreation.resolution?.height
                ? sourceCreation.resolution.width / sourceCreation.resolution.height
                : draft.uploadedVideo?.aspectRatio || 16 / 9,
            cover_url: sourceCreation.cover_uri || draft.uploadedVideo?.cover_url,
            cover_preview_url:
              sourceCreation.cover_uri || draft.uploadedVideo?.cover_preview_url || draft.uploadedVideo?.cover_url,
            width: sourceCreation.resolution?.width || draft.uploadedVideo?.width,
            height: sourceCreation.resolution?.height || draft.uploadedVideo?.height,
          },
          rangeValue: [currentRangeFrom, nextRangeTo],
          formParams: {
            ...draft.formParams,
            videoPrompt: {
              ...draft.formParams.videoPrompt,
              content: draft.formParams.videoPrompt.content || `sscreation:?id=${sourceCreationId}`,
              duration: nextVideoDuration,
              to:
                draft.formParams.videoPrompt.to > draft.formParams.videoPrompt.from
                  ? draft.formParams.videoPrompt.to
                  : sourceDuration,
            },
          },
        };
      });
    };

    void hydrateSourceCreation();

    return () => {
      cancelled = true;
    };
  }, [extensionToolDraft.initializedKey, sourceCreationId, shouldHydrateSourceCreation, updateExtensionToolDraft]);

  useFetch(
    () =>
      getCreationDetail({
        path: {
          id: creationId,
        },
      }),
    {
      ready:
        !!creationId &&
        !hasRecreateParams &&
        !extensionToolDraft.initializedKey?.startsWith('task:') &&
        extensionToolDraft.initializedKey !== `creation:${creationId}`,
      refreshDeps: [creationId, extensionToolDraft.initializedKey, hasRecreateParams],
      onSuccess: ({ data, error }) => {
        if (error || !data?.creation) return;
        if (!is1080pCreation(data.creation)) {
          toast({
            type: 'error',
            message: t('task.extendOnly1080p'),
          });
          router.replace(pathname);
          return;
        }
        replaceExtensionToolDraft(buildExtensionToolDraftFromCreation(data.creation));
      },
    },
  );

  const submitButtonStatus = useMemo(() => {
    const extendLimit = getUserExtendLimit(userInfo?.subs_plan as SubsPlanType);
    const selectedDuration = rangeValue[1] - rangeValue[0];
    const totalExtendDuration = (settings.duration || 0) + selectedDuration;

    if (totalExtendDuration > extendLimit) {
      return SUBMIT_BUTTON_STATUS.OVER_LIMIT;
    }

    if (userInfo?.subs_plan === 'free') {
      const needUpgrade =
        (settings.transition === 'extendspeed' && !trialsQ2Extend) ||
        (settings.transition === 'extendpro' && !trialsQ2ExtendPro);

      if (needUpgrade) {
        return SUBMIT_BUTTON_STATUS.NEED_UPGRADE;
      }
    }

    return SUBMIT_BUTTON_STATUS.ENABLE;
  }, [userInfo, rangeValue, settings.duration, settings.transition, trialsQ2ExtendPro, trialsQ2Extend]);

  const handleCreditsInsufficientClick = useCallback(() => {
    if (isTeamSpace) {
      openConfirmDialog({
        title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
        description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
      return;
    }
    if (isFree) {
      openPlanDetailDialog({
        scene: PlanDialogScene.InsufficientCredits,
        fromPage: ' create button credits insufficient',
      });
      reportEvent('vip_price_modal_exposure', {
        from_page: ' create button credits insufficient',
      });
      sendGAEvent('event', 'subscription_exposure', {
        from_page: ' create button credits insufficient',
      });
      return;
    }

    openPurchaseCreditsDialog({
      showTriggerButton: false,
      onClose: () => {
        tryOpenInsufficientCreditsDialog();
      },
    });
    reportEvent('purchase_credits_exposure', { from_position: `${submitTaskType} -${modelVersion}` });
  }, [
    isFree,
    modelVersion,
    openPlanDetailDialog,
    openPurchaseCreditsDialog,
    submitTaskType,
    tryOpenInsufficientCreditsDialog,
    isTeamSpace,
  ]);

  useEffect(() => {
    const expectedKey = creationId ? `creation:${creationId}` : undefined;
    if (!expectedKey) return;
    if (extensionToolDraft.initializedKey !== expectedKey && !extensionToolDraft.initializedKey?.startsWith('task:')) {
      return;
    }

    router.replace(pathname);
  }, [creationId, extensionToolDraft.initializedKey, pathname, router]);

  const updateTextPrompt = (nextTextPrompt: string) =>
    updateExtensionToolDraft((draft) => ({
      ...draft,
      formParams: {
        ...draft.formParams,
        textPrompt: nextTextPrompt,
      },
    }));

  const updateImagePrompt = (idx: number, imagePrompt: FormImagePrompt | null) => {
    updateExtensionToolDraft((draft) => {
      const list = [...draft.formParams.imagePrompts];
      list[idx] = imagePrompt;
      return {
        ...draft,
        formParams: {
          ...draft.formParams,
          imagePrompts: list,
        },
      };
    });
  };

  const updateSettings = (newSettings: Video2ExtendFormParams['settings']) =>
    updateExtensionToolDraft((draft) => ({
      ...draft,
      formParams: {
        ...draft.formParams,
        settings: { ...draft.formParams.settings, ...newSettings },
      },
    }));

  const clearPrompt = () => updateTextPrompt('');

  const updateSelectedImage = useCallback(
    (idx: number, nextImagePrompt: ImagePrompt) => {
      updateExtensionToolDraft((draft) => {
        const list = [...draft.formParams.imagePrompts];
        const currentImagePrompt = list[idx];
        if (!currentImagePrompt) {
          return draft;
        }

        list[idx] = {
          ...currentImagePrompt,
          status: 'done',
          prompt: nextImagePrompt,
        };

        return {
          ...draft,
          formParams: {
            ...draft.formParams,
            imagePrompts: list,
          },
        };
      });
    },
    [updateExtensionToolDraft],
  );

  const { selectModalImageSource, handleCloseSelectModal, handleOpenSelectModal, handleSaveSelectImage } =
    useImageSelectModal({
      updateSelectImage: updateSelectedImage,
      maxImageSize: MAX_IMAGE_SIZE,
      // 本表单已挂 useRunTempUploadBatch，未登录裁剪只存本地、登录后自动补传
      allowTempMode: true,
    });

  const applyVideoSource = useCallback(
    ({
      content,
      sourceMeta,
      duration,
      creation,
      uploadedVideo,
    }: {
      content: string;
      sourceMeta: Video2ExtendFormParams['videoPrompt']['sourceMeta'];
      duration: number;
      creation?: CreationInfo | null;
      uploadedVideo?: VideoUploadFile['file'] | null;
    }) => {
      const normalizedDuration = Math.ceil(duration || 0);

      updateExtensionToolDraft((draft) => ({
        ...draft,
        creation: sourceMeta.type === 'creation' ? (creation ?? null) : null,
        rangeValue: [0, normalizedDuration],
        uploadedVideo: sourceMeta.type === 'upload' ? (uploadedVideo ?? null) : null,
        formParams: {
          ...draft.formParams,
          videoPrompt: {
            from: 0,
            to: normalizedDuration,
            duration: normalizedDuration,
            content,
            sourceMeta,
          },
        },
      }));
    },
    [updateExtensionToolDraft],
  );

  const clearVideoSource = useCallback(() => {
    updateExtensionToolDraft((draft) => ({
      ...draft,
      creation: null,
      rangeValue: [0, 0],
      uploadedVideo: null,
      formParams: {
        ...draft.formParams,
        videoPrompt: {
          from: 0,
          to: 0,
          duration: 0,
          content: '',
          sourceMeta: {
            type: 'creation',
            creationId: '',
          },
        },
      },
    }));
  }, [updateExtensionToolDraft]);

  const getActiveVideoResolution = useCallback(() => {
    if (formParams.videoPrompt.sourceMeta.type === 'creation' && creation?.resolution) {
      return creation.resolution;
    }

    if (uploadedVideo?.width && uploadedVideo?.height) {
      return {
        width: uploadedVideo.width,
        height: uploadedVideo.height,
      };
    }

    return null;
  }, [creation?.resolution, formParams.videoPrompt.sourceMeta.type, uploadedVideo?.height, uploadedVideo?.width]);

  /** 从 store 读取视频分辨率。全屏并行拖拽时图片 done 回调来自「开始上传时」那次渲染的闭包，闭包里的 uploadedVideo 仍为旧值，必须用 getState。 */
  const getActiveVideoResolutionFromStore = useCallback((): { width: number; height: number } | null => {
    const draft = storeApi.getState().extensionToolDraft;
    const { formParams: fp, creation: c, uploadedVideo: uv } = draft;
    if (fp.videoPrompt.sourceMeta.type === 'creation' && c?.resolution) {
      return c.resolution;
    }
    if (uv?.width && uv?.height) {
      return { width: uv.width, height: uv.height };
    }
    return null;
  }, [storeApi]);

  const ensureImagePromptSize = useCallback(async (imagePrompt: ImagePrompt): Promise<boolean> => {
    if (checkSourceImageSize(imagePrompt)) {
      return true;
    }

    try {
      const { width, height } = await getImageSize(imagePrompt.sourceImagePreview);
      imagePrompt.sourceImageWidth = width;
      imagePrompt.sourceImageHeight = height;
      return true;
    } catch {
      return false;
    }
  }, []);

  const shouldForceCropImage = useCallback(
    (imagePrompt: ImagePromptWithSize, resolution: { width: number; height: number }): boolean => {
      return !checkImageRatiosByRatios([resolution.width / resolution.height, getImageRatio(imagePrompt)]);
    },
    [],
  );

  const activeCropOption = useMemo(() => {
    const activeResolution = getActiveVideoResolution();
    if (!activeResolution) {
      return null;
    }

    return {
      label: formatAspectRatioLabel(activeResolution.width, activeResolution.height),
      value: activeResolution.width / activeResolution.height,
    };
  }, [getActiveVideoResolution]);

  const openForceCropModal = useCallback(
    async ({
      idx,
      imagePrompt,
      fallbackPrompt,
      reason,
    }: {
      idx: number;
      imagePrompt: ImagePrompt;
      fallbackPrompt: FormImagePrompt | null;
      reason: 'upload' | 'video-select';
    }) => {
      forceCropFallbackPromptRef.current = fallbackPrompt;
      forceCropReasonRef.current = reason;
      await handleOpenSelectModal(idx, imagePrompt);
    },
    [handleOpenSelectModal],
  );

  const maybeForceCropExistingImage = useCallback(
    async (resolution: { width: number; height: number }) => {
      // 全屏拖拽会并行上传图+视频：视频 done 回调可能晚于多轮渲染，必须从 store 读最新尾帧，避免闭包里的 imagePrompts 仍为空而跳过裁剪。
      const currentImagePrompt = storeApi.getState().extensionToolDraft.formParams.imagePrompts[0];
      if (!currentImagePrompt?.prompt || currentImagePrompt.status === 'uploading' || selectModalImageSource) {
        return;
      }

      const hasImageSize = await ensureImagePromptSize(currentImagePrompt.prompt);
      if (!hasImageSize || !shouldForceCropImage(currentImagePrompt.prompt as ImagePromptWithSize, resolution)) {
        return;
      }

      await openForceCropModal({
        idx: 0,
        imagePrompt: currentImagePrompt.prompt,
        fallbackPrompt: null,
        reason: 'video-select',
      });
    },
    [ensureImagePromptSize, openForceCropModal, selectModalImageSource, shouldForceCropImage],
  );

  const handleUploadSuccess = async (idx: number, uploadFile: ImageUploadFile) => {
    if (!uploadFile.file) {
      return;
    }

    const uploadedImagePrompt = uploadFile.file;
    const nextImagePrompt: FormImagePrompt = {
      id: uploadFile.id,
      prompt: uploadedImagePrompt,
      status: 'done',
    };
    updateImagePrompt(idx, nextImagePrompt);

    const activeResolution = getActiveVideoResolutionFromStore();
    if (!activeResolution) {
      return;
    }

    const hasImageSize = await ensureImagePromptSize(uploadedImagePrompt);
    if (!hasImageSize || !shouldForceCropImage(uploadedImagePrompt as ImagePromptWithSize, activeResolution)) {
      return;
    }

    await openForceCropModal({
      idx,
      imagePrompt: uploadedImagePrompt,
      fallbackPrompt: preImagePromptRef.current,
      reason: 'upload',
    });
  };

  const handleChangeFiles = (idx: number, uploadFile: ImageUploadFile) => {
    if (uploadFile.status === 'removed') {
      updateImagePrompt(idx, null);
      return;
    }

    if (uploadFile.status === 'uploading') {
      updateImagePrompt(idx, {
        id: uploadFile.id,
        status: 'uploading',
        prompt: null,
      });
      return;
    }

    if (uploadFile.status === 'error') {
      updateImagePrompt(idx, null);
      return;
    }

    void handleUploadSuccess(idx, uploadFile);
  };

  const handleUploadVideo = useCallback(
    (uploadFile: VideoUploadFile) => {
      if (uploadFile.status === 'uploading') {
        setUploadingVideoPrompt({
          id: uploadFile.id,
          status: 'uploading',
          prompt: null,
        });
        return;
      }

      if (uploadFile.status === 'done' && uploadFile.file) {
        setUploadingVideoPrompt(null);
        const file = uploadFile.file;
        const nextUploadedVideo = {
          ...file,
          width: uploadFile.width ?? file.width,
          height: uploadFile.height ?? file.height,
        };
        applyVideoSource({
          sourceMeta: {
            type: 'upload',
            uploadId: file.id || uploadFile.id || '',
          },
          content: file.src || file.preview || '',
          duration: file.duration || 0,
          uploadedVideo: nextUploadedVideo,
        });

        if (nextUploadedVideo.width && nextUploadedVideo.height) {
          void maybeForceCropExistingImage({
            width: nextUploadedVideo.width,
            height: nextUploadedVideo.height,
          });
        }
        return;
      }

      if (uploadFile.status === 'removed' || uploadFile.status === 'error') {
        setUploadingVideoPrompt(null);
        clearVideoSource();
      }
    },
    [applyVideoSource, clearVideoSource, maybeForceCropExistingImage],
  );

  const validateVideoMetadata = useCallback(
    ({ width, height }: { duration: number; aspectRatio: number; width: number; height: number }) => {
      if (!is1080pAreaVideo(width, height)) {
        return t('task.extendOnly1080p');
      }

      return null;
    },
    [t],
  );

  const handleSelectMedia = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedCreationIndex(null);
        return;
      }
      setUploadingVideoPrompt(null);
      setSelectedCreationIndex(index);
      applyVideoSource({
        sourceMeta: {
          type: 'creation',
          creationId: creation.id || '',
        },
        content: `sscreation:?id=${creation.id || ''}`,
        duration: creation.duration || 0,
        creation,
      });
      if (creation.resolution) {
        void maybeForceCropExistingImage(creation.resolution);
      }
      setOpenMediaSelector(false);
    },
    [applyVideoSource, maybeForceCropExistingImage],
  );

  const applyEndFrameCreationSelection = useCallback(
    async (nextCreation: CreationWithTask) => {
      const fallbackPrompt = formParams.imagePrompts[0] ?? null;
      const nextImagePrompt = createImagePromptFromCreation(nextCreation, { includeReference: true });
      updateImagePrompt(0, nextImagePrompt);

      const activeResolution = getActiveVideoResolution();
      if (!activeResolution) {
        return;
      }

      const nextPrompt = nextImagePrompt.prompt;
      if (!nextPrompt) {
        return;
      }

      const hasImageSize = await ensureImagePromptSize(nextPrompt);
      if (!hasImageSize || !shouldForceCropImage(nextPrompt as ImagePromptWithSize, activeResolution)) {
        return;
      }

      await openForceCropModal({
        idx: 0,
        imagePrompt: nextPrompt,
        fallbackPrompt,
        reason: 'video-select',
      });
    },
    [
      ensureImagePromptSize,
      formParams.imagePrompts,
      getActiveVideoResolution,
      openForceCropModal,
      shouldForceCropImage,
      updateImagePrompt,
    ],
  );

  const handleSelectEndFrameMedia = useCallback((index: number | null, nextCreation?: CreationWithTask) => {
    if (!nextCreation) {
      setSelectedEndFrameCreationIndex(null);
      setSelectedEndFrameCreation(null);
      return;
    }

    setSelectedEndFrameCreationIndex(index);
    setSelectedEndFrameCreation(nextCreation);
  }, []);

  const handleRemoveSourceMedia = useCallback(() => {
    setUploadingVideoPrompt(null);
    clearVideoSource();
    setSelectedCreationIndex(null);
  }, [clearVideoSource]);

  const handleRemoveEndFrameMedia = useCallback(() => {
    updateImagePrompt(0, null);
    setSelectedEndFrameCreationIndex(null);
    setSelectedEndFrameCreation(null);
  }, [updateImagePrompt]);

  const handleOpenEndFrameSelector = useCallback(() => {
    setSelectedEndFrameCreationIndex(null);
    setSelectedEndFrameCreation(null);
    setOpenEndFrameSelector(true);
  }, []);

  const videoSrc = useMemo(() => {
    if (!creation) return '';
    if (SITE === 'china') return creation.uri;
    return userInfo?.subs_plan !== 'free' && creation.nomark_uri ? creation.nomark_uri : creation.uri;
  }, [creation, userInfo]);
  const activeVideoSrc = useMemo(() => {
    if (formParams.videoPrompt.sourceMeta.type === 'creation' && creation?.resolution) {
      return videoSrc;
    }

    return uploadedVideo?.preview || uploadedVideo?.src || '';
  }, [
    creation?.resolution,
    formParams.videoPrompt.sourceMeta.type,
    uploadedVideo?.preview,
    uploadedVideo?.src,
    videoSrc,
  ]);
  const activeVideoDuration = useMemo(() => {
    if (formParams.videoPrompt.duration > 0) {
      return formParams.videoPrompt.duration;
    }

    if (formParams.videoPrompt.sourceMeta.type === 'creation' && creation?.resolution) {
      return totalDuration;
    }

    return Math.ceil(uploadedVideo?.duration || 0);
  }, [
    creation?.resolution,
    formParams.videoPrompt.duration,
    formParams.videoPrompt.sourceMeta.type,
    totalDuration,
    uploadedVideo?.duration,
  ]);
  const activeVideoAspectRatio = useMemo(() => {
    if (formParams.videoPrompt.sourceMeta.type === 'creation' && creation?.resolution) {
      return creation.resolution.width / creation.resolution.height;
    }

    return uploadedVideo?.aspectRatio || 16 / 9;
  }, [
    creation?.resolution,
    creation?.resolution?.height,
    creation?.resolution?.width,
    formParams.videoPrompt.sourceMeta.type,
    uploadedVideo?.aspectRatio,
  ]);
  const showVideoFrameSelector = !!activeVideoSrc && activeVideoDuration > 0;
  const previewVideoPrompt = useMemo(() => {
    if (uploadingVideoPrompt) {
      return uploadingVideoPrompt;
    }

    if (!uploadedVideo) {
      return null;
    }

    return {
      id: uploadedVideo.id || 'original',
      status: 'done' as const,
      prompt: {
        video: uploadedVideo.src || uploadedVideo.preview,
        videoPreview: uploadedVideo.preview || uploadedVideo.src,
        duration: uploadedVideo.duration,
        coverImage: uploadedVideo.cover_url || uploadedVideo.cover_preview_url,
        coverImagePreview: uploadedVideo.cover_preview_url || uploadedVideo.cover_url || uploadedVideo.preview,
      },
    };
  }, [uploadedVideo, uploadingVideoPrompt]);

  const hasAvailableVideoSource = !!formParams.videoPrompt.content;
  const hasUploadingImages = formParams.imagePrompts.some((prompt) => prompt?.status === 'uploading');
  const isSubmitUploading = hasUploadingImages || isTempMediaUploading;

  useEffect(() => {
    if (!showVideoFrameSelector) {
      hasTriggeredExtendTourRef.current = false;
      return;
    }

    if (hasTriggeredExtendTourRef.current) {
      return;
    }

    activateNextTour(TOUR_KEYS.EXTEND_TOUR);
    hasTriggeredExtendTourRef.current = true;
  }, [activateNextTour, showVideoFrameSelector]);

  return (
    <>
      <ToolFormContainer
        title={t('create.extendForm.title')}
        description={t('create.extendForm.description')}
        className="h-full overflow-y-auto pb-[72px] sm:min-h-[420px]"
        contentClassName="gap-4"
        exampleSection={<ExampleMediaPreview tool="video2extend" />}
        mediaSection={
          <MediaDropZone
            acceptVideo={true}
            acceptImage={true}
            maxVideoCount={showVideoFrameSelector || uploadingVideoPrompt ? 0 : 1}
            maxImageCount={imagePrompts[0] ? 0 : 1}
            disabled={!!(showVideoFrameSelector || uploadingVideoPrompt) && !!imagePrompts[0]}
            maxVideoSize={Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE}
            maxVideoDuration={EXTEND_UPLOAD_MAX_VIDEO_DURATION_IN_SECONDS}
            minVideoDuration={EXTEND_MIN_VIDEO_DURATION_IN_SECONDS}
            videoAccept={EXTEND_ACCEPT_VIDEO_MIMETYPE}
            getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize: MAX_IMAGE_SIZE })}
            allowTempMode={true}
            enablePaste={true}
            onChange={(file, type) => {
              if (type === 'video') handleUploadVideo(file as VideoUploadFile);
              if (type === 'image') {
                preImagePromptRef.current = imagePrompts[0] || null;
                handleChangeFiles(0, file as ImageUploadFile);
              }
            }}
          >
            <div className="relative grid flex-shrink-0 grid-cols-2 items-stretch gap-3 sm:grid-cols-1 sm:gap-4">
              {showVideoFrameSelector ? (
                <div className="flex min-h-0 flex-1 flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <AgentLineGenerateVideo className="size-5 text-system-white" />
                    <p className="font-medium text-base text-system-white leading-[1.4]">{t('tools.targetVideo')}</p>
                  </div>
                  <VideoPreview
                    videoSrc={activeVideoSrc}
                    totalDuration={activeVideoDuration}
                    rangeValue={rangeValue}
                    onRangeChange={(value) => {
                      updateExtensionToolDraft((draft) => ({
                        ...draft,
                        rangeValue: value,
                        formParams: {
                          ...draft.formParams,
                          videoPrompt: {
                            ...draft.formParams.videoPrompt,
                            from: value[0],
                            to: value[1],
                          },
                        },
                      }));
                    }}
                    aspectRatio={activeVideoAspectRatio}
                    minGapToast={t('create.extendForm.minGapToast', { num: 2 })}
                    isMobile={isMobile}
                    onRemoveMedia={handleRemoveSourceMedia}
                  />
                </div>
              ) : (
                <ToolMediaUpload
                  type="video"
                  header={
                    <ToolMediaUploadHeader
                      icon={
                        <AgentLineGenerateVideo className="size-5 shrink-0 text-system-white md:hidden lg:size-4" />
                      }
                      title={t('tools.targetVideo')}
                    />
                  }
                  surfaceVariant="compact"
                  surfaceSize="sm"
                  isMobile={isMobile}
                  maxVideoSize={Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE}
                  maxDuration={EXTEND_UPLOAD_MAX_VIDEO_DURATION_IN_SECONDS}
                  minDuration={EXTEND_MIN_VIDEO_DURATION_IN_SECONDS}
                  videoAccept={EXTEND_ACCEPT_VIDEO_MIMETYPE}
                  videoPrompt={previewVideoPrompt}
                  onOpenSelector={() => setOpenMediaSelector(true)}
                  onRemoveMedia={handleRemoveSourceMedia}
                  validateVideoMetadata={validateVideoMetadata}
                  onVideoUpload={handleUploadVideo}
                />
              )}

              <ProductFillFrames className="mx-auto hidden size-7 rotate-90 backdrop-blur-[2px] sm:block" />

              <ToolMediaUpload
                type="image"
                imagePrompt={imagePrompts[0] ?? null}
                isMobile={isMobile}
                showImageWatermark={
                  Boolean(imagePrompts[0]?.reference?.creation_id) &&
                  (SITE === 'china' || userInfo?.subs_plan === 'free')
                }
                getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize: MAX_IMAGE_SIZE })}
                onImageUpload={(file) => {
                  preImagePromptRef.current = imagePrompts[0] || null;
                  handleChangeFiles(0, file);
                }}
                surfaceSize="sm"
                onRemoveMedia={handleRemoveEndFrameMedia}
                onOpenSelector={handleOpenEndFrameSelector}
                header={
                  <ToolMediaUploadHeader
                    icon={<ProductLinePic className="size-5 shrink-0 text-system-white md:hidden lg:size-4" />}
                    title={t('create.createForm.upload.lastFrame.tab')}
                    extra={
                      <>
                        <CustomTooltip content={t('create.extendForm.lastFrame.tooltip')}>
                          <BaseLineInfo className="size-3 text-system-white" />
                        </CustomTooltip>
                        <span className="text-system-white/40 text-xs leading-[1.4]">
                          ({t('elements.elementDialog.optional')})
                        </span>
                      </>
                    }
                  />
                }
                surfaceVariant="compact"
              />

              <ProductFillFrames className="-translate-x-1/2 -translate-y-1/2 absolute top-[calc(50%+15px)] left-1/2 z-10 size-7 backdrop-blur-[2px] sm:hidden" />
            </div>
          </MediaDropZone>
        }
        settingsSection={
          <>
            <div className="h-[140px] rounded-10 border border-system-white08 bg-system-white04 p-3 text-sm backdrop-blur-[30px] sm:text-sm lg:text-xs">
              <BasicTextarea
                placeholder={t('create.extendForm.input.placeholder')}
                textPrompt={textPrompt}
                updateTextPrompt={updateTextPrompt}
                showClearButton={!!textPrompt}
                onClearPrompt={clearPrompt}
              />
            </div>
            <ToolSettingsWithConfig
              includedOptions={includedSettingOptions}
              useAccordion={isMobile}
              accordionTitle={isMobile ? t('create.createForm.settings.title') : ''}
              defaultExpanded={!isMobile}
              optionsClassName={[
                'flex-[1.5] after:hidden',
                'flex-[2.5] after:hidden',
                'flex-1 after:hidden',
                'flex-1 after:hidden',
              ]}
              optionClassName="flex-col gap-2"
              optionsWrapperClassName="gap-6"
              modelVersionOption={
                <ToolSettingContainer title={t('create.createForm.settings.modelVersion.settingTitle')}>
                  <ExtendModelSelection
                    value={settings?.transition}
                    options={settingsConfig?.transition ?? []}
                    onChange={(val) =>
                      updateSettings({
                        transition: val as Video2ExtendFormParams['settings']['transition'],
                      })
                    }
                  />
                </ToolSettingContainer>
              }
              amountOption={{
                value: settings?.sample_count ?? 1,
                options: settingsConfig?.amount ?? [],
                onChange: (val) => {
                  updateSettings({ sample_count: val });
                },
                onShouldUpgrade: (key) =>
                  handleOpenPlanDialog(generateFromPage({ modelVersion, type: 'video2extend', key })),
                checkOptionValue: settingsConfig?.checkOptionValue
                  ? (args) => Boolean(settingsConfig.checkOptionValue?.(args))
                  : undefined,
              }}
              resolutionOption={{
                value: settings?.resolution ?? '1080p',
                options: settingsConfig?.resolution ?? [],
                onChange: (val) => {
                  updateSettings({ resolution: val });
                },
                onShouldUpgrade: (key) => handleOpenPlanDialog(`settings ${key}`),
              }}
              durationOption={{
                value: settings?.duration,
                options: settingsConfig?.duration ?? [],
                onChange: (val) => updateSettings({ duration: val }),
                title: t('create.createForm.settings.duration.extendTitle'),
                onShouldUpgrade: (key) =>
                  handleOpenPlanDialog(generateFromPage({ modelVersion, type: 'video2extend', key })),
              }}
            />
          </>
        }
        submitButton={
          <div className="w-full max-w-[680px]">
            <ToolFormSubmitButton
              disabledTip={
                (isSubmitUploading ? t('create.createForm.error.uploadingMedia') : '') ||
                (!hasAvailableVideoSource ? t('elements.actionMsg.creationNotExists') : '') ||
                formValidationErrorMessage ||
                (submitButtonStatus === SUBMIT_BUTTON_STATUS.OVER_LIMIT
                  ? isUltimateOrHigherPlan(userInfo?.subs_plan)
                    ? t('create.extendForm.overlimit')
                    : t.rich('create.extendForm.upgrade.title', {
                        upgrade: (chunks) => (
                          <span
                            className="cursor-pointer text-system-blue02"
                            onClick={() => handleOpenPlanDialog?.('extend_upgrade')}
                          >
                            {chunks}
                          </span>
                        ),
                      })
                  : '')
              }
              taskType={submitTaskType}
              submitValue={submitValue}
              inZeroCreditsTrials={isInTrials}
              onCreditsInsufficientClick={handleCreditsInsufficientClick}
              disabled={
                isSubmitUploading || !hasAvailableVideoSource || submitButtonStatus !== SUBMIT_BUTTON_STATUS.ENABLE
              }
              displayOriginalCredits={false}
            >
              {t('create.createForm.submit')}
            </ToolFormSubmitButton>
          </div>
        }
      />
      <CreationSelectorDialog
        open={openMediaSelector}
        onOpenChange={setOpenMediaSelector}
        selectedIndex={selectedCreationIndex}
        mediaType="video"
        minDuration={EXTEND_MIN_VIDEO_DURATION_IN_SECONDS}
        isCreationDisabled={(creation) => !is1080pCreation(creation)}
        getDisabledReason={(creation) => (!is1080pCreation(creation) ? t('task.extendOnly1080p') : null)}
        onSelectionChange={handleSelectMedia}
        onConfirm={() => {}}
        onViewCreation={(_creation) => {}}
      />
      <CreationSelectorDialog
        open={openEndFrameSelector}
        onOpenChange={setOpenEndFrameSelector}
        selectedIndex={selectedEndFrameCreationIndex}
        mediaType="image"
        onSelectionChange={handleSelectEndFrameMedia}
        onConfirm={() => {
          setOpenEndFrameSelector(false);
          if (selectedEndFrameCreation) {
            void applyEndFrameCreationSelection(selectedEndFrameCreation);
          }
        }}
        onViewCreation={(_creation) => {}}
      />
      <TemplateImgCropModal
        onClose={() => {
          handleCloseSelectModal();

          if (forceCropReasonRef.current) {
            updateImagePrompt(0, forceCropFallbackPromptRef.current);
          }

          forceCropReasonRef.current = null;
          forceCropFallbackPromptRef.current = null;
        }}
        onConfirm={async (range) => {
          forceCropReasonRef.current = null;
          forceCropFallbackPromptRef.current = null;
          await handleSaveSelectImage(range);
        }}
        imageSource={selectModalImageSource}
        cropOptions={activeCropOption ? [activeCropOption] : undefined}
        title={t('imgCropModal.title')}
        description={t('create.extendForm.lastFrame.imgAspectRatioError')}
      />
      {showVideoFrameSelector && (
        <FeatureTour
          tourConfig={{
            modal: true,
            items: [
              {
                domId: 'extend-video-frame',
                side: 'top',
                align: 'center',
                sideOffset: 5,
                content: (
                  <div className="flex w-full items-center gap-4">
                    <img
                      src={
                        SITE === 'china'
                          ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/extend_guide-5b660f41.webp'
                          : 'https://image01.cf.vidu.studio/vidu/media-asset/extend_guide-5b660f41.webp'
                      }
                      alt="slide-tooltip"
                      className="h-[56px] w-[84px] object-cover"
                    />
                    <div className="text-sm text-system-white">{t('create.extendForm.slide.tooltip')}</div>
                  </div>
                ),
                isInHeader: false,
                popoverCtxClassName: 'bg-[#0062FF] w-[396px]',
                popoverCtxInnerClassName: 'pr-10',
                disableHighLight: true,
              },
            ],
          }}
          tourKey={TOUR_KEYS.EXTEND_TOUR}
        />
      )}
    </>
  );
};

export default ExtensionForm;
